import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  LocationConceptService,
  ConceptStatusDto,
  BulkCreateLocationConceptsRequest,
  ConceptToCreateDto,
  StaffAssignmentDto
} from '../../../services/location-concept.service';
import { MasterModel } from '../../../../../master/models/master.model';

interface DialogData {
  locationId: string;
  availableConcepts: MasterModel[];
  availableStaff: MasterModel[];
  existingConceptIds: string[];
  conceptStatuses: ConceptStatusDto[];
}

@Component({
    selector: 'app-location-service-bulk-add-dialog',
    templateUrl: './location-service-bulk-add-dialog.component.html',
    styleUrls: ['./location-service-bulk-add-dialog.component.css'],
    standalone: false
})
export class LocationServiceBulkAddDialogComponent implements OnInit {
  searchTerm = '';
  filteredConcepts: MasterModel[] = [];
  selectedConceptIds: string[] = []; 
  
  defaultsForm: UntypedFormGroup;
  
  isLoading = false;
  errorMessage: string | null = null;
  
  isCreating = false;
  createProgress = 0;
  createTotal = 0;
  createSuccessCount = 0;
  createFailureCount = 0;
  creationResults: Array<{conceptName: string, success: boolean, error?: string}> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private conceptService: LocationConceptService,
    public dialogRef: MatDialogRef<LocationServiceBulkAddDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  ngOnInit(): void {
    this.buildDefaultsForm();
    this.applyFilter();
  }

  private buildDefaultsForm(): void {
    this.defaultsForm = this.fb.group({
      durationMinutes: [30, [Validators.required, Validators.min(1)]],
      bufferBeforeMinutes: [0, Validators.min(0)],
      bufferAfterMinutes: [0, Validators.min(0)],
      statusMerchantConceptId: [1, Validators.required],
      staffUids: [[]]
    });
  }

  private getSelectedStaffAssignments(staffUids: string[]): StaffAssignmentDto[] {
    if (!staffUids || staffUids.length === 0) {
      return [];
    }

    return staffUids
      .map(uid => {
        const staff = this.data.availableStaff.find(s => s.id === uid);
        if (!staff) return null;

        const metadata = staff.metadata || {};
        const displayName = staff.display;
        const nameParts = displayName.split(' ');
        const firstName = metadata.firstName || nameParts[0] || displayName;
        const lastName = metadata.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

        return {
          employeeId: staff.id,
          firstName: firstName,
          lastName: lastName,
          displayName: displayName,
          email: metadata.email || null,
          phone: metadata.phone || null
        };
      })
      .filter(s => s !== null) as StaffAssignmentDto[];
  }

  applyFilter(): void {
    let concepts = this.data.availableConcepts.filter(
      concept => !this.isAlreadyAdded(concept.id)
    );

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      concepts = concepts.filter(concept => {
        const metadata = concept.metadata || {};
        return concept.display.toLowerCase().includes(term) ||
               (metadata.description && metadata.description.toLowerCase().includes(term));
      });
    }

    this.filteredConcepts = concepts;
  }

  isSelected(conceptId: string): boolean {
    return this.selectedConceptIds.includes(conceptId);
  }

  isAlreadyAdded(conceptId: string): boolean {
    return this.data.existingConceptIds.includes(conceptId);
  }

  toggleSelection(conceptId: string): void {
    const index = this.selectedConceptIds.indexOf(conceptId);
    if (index > -1) {
      this.selectedConceptIds.splice(index, 1);
    } else {
      this.selectedConceptIds.push(conceptId);
    }
  }

  toggleSelectAll(): void {
    const allSelected = this.filteredConcepts.every(c => this.isSelected(c.id));
    
    if (allSelected) {
      this.filteredConcepts.forEach(c => {
        const index = this.selectedConceptIds.indexOf(c.id);
        if (index > -1) {
          this.selectedConceptIds.splice(index, 1);
        }
      });
    } else {
      this.filteredConcepts.forEach(c => {
        if (!this.isSelected(c.id)) {
          this.selectedConceptIds.push(c.id);
        }
      });
    }
  }

  get allSelected(): boolean {
    return this.filteredConcepts.length > 0 && 
           this.filteredConcepts.every(c => this.isSelected(c.id));
  }

  onAdd(): void {
    if (this.selectedConceptIds.length === 0) {
      return;
    }

    if (!this.defaultsForm.valid) {
      this.markFormGroupTouched(this.defaultsForm);
      return;
    }

    this.isCreating = true;
    this.errorMessage = null;
    this.createProgress = 0;
    this.createTotal = this.selectedConceptIds.length;
    this.createSuccessCount = 0;
    this.createFailureCount = 0;
    this.creationResults = [];

    const defaults = this.defaultsForm.value;
    const staffAssignments = this.getSelectedStaffAssignments(defaults.staffUids || []);

    const conceptsToCreate: ConceptToCreateDto[] = this.selectedConceptIds.map(conceptId => {
      const concept = this.data.availableConcepts.find(c => c.id === conceptId);
      const metadata = concept && concept.metadata ? concept.metadata : {};

      return {
        conceptId: conceptId,
        title: concept ? concept.display : 'Sin título',
        description: metadata.description || null,
        durationMinutes: metadata.defaultDuration || defaults.durationMinutes,
        price: metadata.price !== undefined && metadata.price !== null ? metadata.price : 0,
        bufferBeforeMinutes: defaults.bufferBeforeMinutes,
        bufferAfterMinutes: defaults.bufferAfterMinutes,
        statusMerchantConceptId: defaults.statusMerchantConceptId,
        staffAssignments: staffAssignments
      };
    });

    const bulkRequest: BulkCreateLocationConceptsRequest = {
      concepts: conceptsToCreate
    };

    this.conceptService.bulkCreateLocationConcepts(this.data.locationId, bulkRequest).subscribe(
      response => {
        this.isCreating = false;
        
        if (response.isSuccess && response.value) {
          this.createSuccessCount = response.value.successCount;
          this.createFailureCount = response.value.failedCount;
          
          const successResults = response.value.createdConcepts.map(created => ({
            conceptName: created.title,
            success: true
          }));
          
          const errorResults = response.value.errors.map(error => ({
            conceptName: error.title || 'Desconocido',
            success: false,
            error: error.errorMessage
          }));
          
          this.creationResults = [...successResults, ...errorResults];

          if (this.createFailureCount === 0) {
            this.dialogRef.close(true);
          }
        } else {
          this.errorMessage = this.getErrorMessage(response.error);
        }
      },
      error => {
        this.isCreating = false;
        this.errorMessage = 'Error al crear los conceptos. Inténtalo de nuevo.';
        console.error('Error bulk creating concepts:', error);
      }
    );
  }

  removeSelected(conceptId: string): void {
    const index = this.selectedConceptIds.indexOf(conceptId);
    if (index > -1) {
      this.selectedConceptIds.splice(index, 1);
    }
  }

  getConceptName(conceptId: string): string {
    const concept = this.data.availableConcepts.find(c => c.id === conceptId);
    return concept ? concept.display : 'Desconocido';
  }

  private getErrorMessage(error: any): string {
    if (!error) return 'Error desconocido';
    
    if (error.messages && error.messages.length > 0) {
      return error.messages.join(', ');
    }
    
    return error.message || 'Error al procesar la solicitud';
  }

  clearError(): void {
    this.errorMessage = null;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onRetry(): void {
    this.dialogRef.close(true);
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();
    });
  }
  public trackByIndex(i: number) { return i; }

}

